import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { type Language } from '../../../config/localization';
import CropAdjustmentPanel, { type CropViewState } from './CropAdjustmentPanel';

interface CropAdjustmentModalProps {
  isOpen: boolean;
  originalUrl: string;
  targetWidth: number;
  targetHeight: number;
  convertToJpeg?: boolean;
  initialView?: CropViewState;
  onApply: (
    croppedFile: File,
    blobUrl: string,
    dimensions: { width: number; height: number },
    view: CropViewState
  ) => void;
  onCancel: () => void;
  language: Language;
}

/** Standalone crop modal: portal + backdrop + card around {@link CropAdjustmentPanel}. */
export default function CropAdjustmentModal({
  isOpen,
  originalUrl,
  targetWidth,
  targetHeight,
  convertToJpeg,
  initialView,
  onApply,
  onCancel,
  language,
}: CropAdjustmentModalProps) {
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onCancel();
    },
    [onCancel]
  );

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl flex flex-col w-full max-w-4xl mx-4 h-[90vh]">
        <CropAdjustmentPanel
          originalUrl={originalUrl}
          targetWidth={targetWidth}
          targetHeight={targetHeight}
          convertToJpeg={convertToJpeg}
          initialView={initialView}
          onApply={onApply}
          onCancel={onCancel}
          language={language}
        />
      </div>
    </div>,
    document.body
  );
}
